import React, { useEffect, useRef, useState } from "react";
import { loadStripeJs } from "./funnel/FunnelCheckout";
import { logger } from "../utils/logger";

/**
 * Stripe Embedded Checkout: Stripe's own card form, mounted inside our page
 * (the same thing the web funnel uses). Card details never pass through
 * Pixley; the page only ever holds the session's client secret, in React
 * state for the life of the form — never in storage, analytics or logs.
 *
 * `onComplete` fires when Stripe says the payment went through;
 * `onUnavailable` when the form could not load here (the caller then falls
 * back to Stripe's hosted page).
 */
export const EmbeddedCheckout = ({ clientSecret, publishableKey, onComplete, onUnavailable }) => {
  const mountRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  const onUnavailableRef = useRef(onUnavailable);
  const [state, setState] = useState("loading");   // 'loading' | 'ready' | 'failed'
  useEffect(() => {
    onCompleteRef.current = onComplete;
    onUnavailableRef.current = onUnavailable;
  }, [onComplete, onUnavailable]);

  useEffect(() => {
    if (!clientSecret || !publishableKey) return undefined;
    let cancelled = false;
    let checkout = null;
    (async () => {
      try {
        const Stripe = await loadStripeJs();
        if (cancelled) return;
        const stripe = Stripe(publishableKey);
        checkout = await stripe.initEmbeddedCheckout({
          clientSecret,
          onComplete: () => onCompleteRef.current?.(),
        });
        if (cancelled) {
          checkout.destroy();
          return;
        }
        checkout.mount(mountRef.current);
        setState("ready");
      } catch (error) {
        // Never log the secret; the error class is enough to diagnose
        logger.warn("Embedded Checkout unavailable:", error?.name || "Error");
        if (!cancelled) {
          setState("failed");
          onUnavailableRef.current?.(error);
        }
      }
    })();
    return () => {
      cancelled = true;
      try {
        checkout?.destroy();
      } catch (_) {
        // already gone
      }
    };
  }, [clientSecret, publishableKey]);

  const bar = (extra = {}) => ({
    display: "block", height: 46, borderRadius: 12,
    background: "linear-gradient(90deg, #EEF2FA 0%, #F8FAFE 50%, #EEF2FA 100%)", backgroundSize: "200% 100%",
    animation: "tw-shimmer 1.3s linear infinite", ...extra,
  });
  return (
    <div style={{ width: "100%", position: "relative", minHeight: 360 }} data-testid="embedded-checkout">
      {state === "loading" && (
        <div role="status" aria-label="Loading secure checkout" style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", gap: 14, padding: "6px 2px" }}>
          <span style={bar()} /><span style={bar()} /><span style={bar({ width: "55%" })} />
          <span style={bar({ height: 52, marginTop: 10, background: "linear-gradient(90deg, #DCE7FF 0%, #EAF1FF 50%, #DCE7FF 100%)", backgroundSize: "200% 100%" })} />
        </div>
      )}
      {state === "failed" && (
        <p role="alert" style={{ fontSize: 13.5, color: "#5A6B85", textAlign: "center", margin: "24px 0" }}>
          The card form couldn't load here. Taking you to Stripe's secure page…
        </p>
      )}
      <div ref={mountRef} style={{ width: "100%" }} />
    </div>
  );
};

export default EmbeddedCheckout;
